import {
  actionsKeys,
  formatAmount,
  invoiceActionStatus,
  permissionActions,
} from "@/lib/utils";
import { setCurrentFinanceYear } from "@/redux/slices";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import {
  useGetInvoiceStatus,
  useGetPositionApprovalLimits,
} from "@/services";
import {
  FileChartColumn,
  FileClock,
  FileInput,
  FileLock2,
  FileSearch,
  FileX2,
  MailOpen,
  Send,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "@/components/Toast";

const useInvoice = () => {
  const navigate = useNavigate();
  const [openTabs, setOpenTabs] = useState<string[]>(["invoice"]);
  const dispatch = useDispatch();
  const accessPermission = useSelector(
    (state: RootState) => state.currentData.accessPermission
  );

  const communityData = useSelector(
    (state: RootState) => state.currentData.communityData
  );

  const currentFinanceYear = useSelector(
    (state: RootState) => state.currentData.currentFinanceYear
  );

  const [invoiceCards, setInvoiceCards] = useState([
    {
      id: "create",
      title: "Create Invoice",
      link: `${routes.add}`,
      module: actionsKeys.INVOICE?.toLowerCase(),
      accessKeys: permissionActions.CREATE,
    },
    {
      id: "my_approval",
      count: 0,
      desc: (
        <div>
          {"Invoices & "} <b>00</b>{" "}
          {"line items awaiting Submission for my Approval"}
        </div>
      ),
      title: "Pending for My Approval",
      icon: Send,
      link: `${invoiceActionStatus.pending_for_my_approval}`,
      apiKey: "pendingForBMApproval",
      module: actionsKeys.INVOICE?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
    {
      id: "board_approval",
      count: 0,
      desc: "Invoices pending for Board approval",
      title: "Pending for Board Approval",
      icon: FileClock,
      link: `${invoiceActionStatus.pending_for_board_approval}`,
      apiKey: "pendingForBoardApproval",
      module: actionsKeys.INVOICE?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
    {
      id: "agm_approval",
      count: 0,
      desc: "Invoices pending for AGM approval",
      title: "Pending For AGM Approval",
      icon: MailOpen,
      link: `${invoiceActionStatus.pending_for_agm_approval}`,
      apiKey: "pendingForAGMApproval",
      module: actionsKeys.INVOICE?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
    {
      id: "submit_for_approval",
      count: 0,
      desc: "Invoices awaiting Submission for Approval",
      title: "Submit for Approval",
      icon: Send,
      link: `${invoiceActionStatus.draft}`, // park
      subtitle: "Parked Documents",
      subtitleColor: "text-yellow-600",
      apiKey: "draft",
      module: actionsKeys.INVOICE?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
    // {
    //   id: "followup_for_approval",
    //   count: 0,
    //   desc: "Invoices requires follow-up for Approval",
    //   title: "Follow-up for Approval",
    //   icon: FileClock,
    //   link: `${invoiceActionStatus.pending_for_approval}`, // pending for approval
    //   subtitle: "Parked Documents",
    //   subtitleColor: "text-yellow-600",
    //   apiKey: "pendingForApproval",
    //   accessKeys: permissionActions.READ,
    // },
    {
      id: "post_invoice",
      count: 0,
      desc: "Invoices ready for Posting",
      title: "Ready for Posting",
      icon: MailOpen,
      link: `${invoiceActionStatus.approved}`,
      subtitle: "Parked Documents",
      subtitleColor: "text-yellow-600",
      apiKey: "approved",
      module: actionsKeys.INVOICE?.toLowerCase(),
      accessKeys: permissionActions.READ_POSTED,
    },
    {
      id: "posted_invoice",
      count: 0,
      desc: "Invoices Posted",
      title: "Posted Invoice",
      icon: MailOpen,
      link: `${invoiceActionStatus.posted}`,
      subtitle: "Posted Documents",
      subtitleColor: "text-green-600",
      apiKey: "postedInvoices",
      module: actionsKeys.INVOICE?.toLowerCase(),
      accessKeys: permissionActions.READ_POSTED,
    },
    {
      id: "summary",
      data: [
        { label: "# Total", value: 0, apiKey: "total" },
        { label: "# Parked", value: 0, apiKey: "parked" },
        { label: "# Pending Approval", value: 0, apiKey: "pendingForApproval" },
        { label: "# Approved", value: 0, apiKey: "approved" },
        { label: "# Posted", value: 0, apiKey: "postedInvoices" },
      ],
      title: "All Invoices",
      link: `all`,
      module: actionsKeys.INVOICE?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
    {
      id: "reports",
      desc: "Invoice 360 Report",
      title: "Reports",
      icon: FileChartColumn,
      link: `${routes.reports}`,
      subtitle: "",
      subtitleColor: "text-green-600",
      apiKey: "",
      hideCount: true,
      module: actionsKeys.INVOICE_REPORT?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
  ]);

  const [userCardList, setUserCardList] = useState<any>([
    {
      title: communityData?.bomPosition?.positionName,
      subTitle: "Position",
      icon: FileSearch,
    },
    {
      title: "",
      subTitle: "Approved Upto",
      icon: FileInput,
      apiKey: "approvalLimit",
    },
    {
      title: "",
      subTitle: "Yearly Limit",
      icon: FileInput,
      apiKey: "totalLimit",
    },
    {
      title: "",
      subTitle: "Approved Amt.",
      icon: FileX2,
      apiKey: "approvedToDate",
    },
    {
      title: "",
      subTitle: "Remaining Amt.",
      icon: FileLock2,
      apiKey: "remainingLimit",
    },
  ]);

  const breadcrumbData = useMemo(
    () => [
      {
        link: `/${routes.bom}/${routes.finance}`,
        name: `Activity Dashboard`,
      },
      {
        link: "",
        name: <span className="text-primary">{"Invoice"}</span>,
      },
    ],
    []
  );

  const { data: userData, isLoading: isUserDataLoading } =
    useGetPositionApprovalLimits({
      positionId: communityData?.bomPosition?.bomPositionId,
      year: currentFinanceYear,
      queryParams: {
        enabled: !!(
          communityData?.bomPosition?.bomPositionId && currentFinanceYear
        ),
      },
    });

  useEffect(() => {
    if (userData) {
      const updatedList = userCardList.map((card: any) => ({
        ...card,
        ...(card?.apiKey
          ? {
              title:
                card?.apiKey != "positionName"
                  ? formatAmount(userData?.[card.apiKey])
                  : userData?.[card.apiKey] || "",
            }
          : {}),
      }));

      setUserCardList(updatedList);
    }
  }, [userData]);

  const onYearChange = (e: any) => {
    dispatch(setCurrentFinanceYear(e));
  };

  const onAccordionChange = (e: any) => {
    setOpenTabs(e);
  };

  const onExpandCollapse = () => {
    if (openTabs?.length) {
      setOpenTabs([]);
    } else {
      setOpenTabs(["payment", "invoice"]);
    }
  };

  const onCreateCardClick = (link: string) => () => {
    if (communityData) {
      navigate(link);
    } else {
      toast.error("Please select community");
    }
  };

  const onCardClick = (link: string) => () => {
    navigate(link);
  };

  const onBackClick = () => {
    navigate(`/${routes.bom}/${routes.finance}`);
  };

  const {
    data: invoiceCount,
    isLoading: isInvoiceCountLoading,
    refetch: refetchInvoiceCount,
    isRefetching: isRefetchingInvoiceCount,
  } = useGetInvoiceStatus({
    communityId: communityData?.communityId,
    year: currentFinanceYear,
    queryParams: {
      enabled: !!(communityData?.communityId && currentFinanceYear),
    },
  });

  useEffect(() => {
    refetchInvoiceCount();
  }, [currentFinanceYear]);

  useEffect(() => {
    if (invoiceCount) {
      const cards = [...invoiceCards];
      const _cards = cards?.map((data: any) => {
        if (data?.id == "summary") {
          const rawData = (invoiceCount as any)?.data || {};

          const statData: any = Object.fromEntries(
            Object.entries(rawData).filter(([key]) => key !== "vpApproved")
          );

          const total = Object.values(statData)?.reduce(
            (acc: any, val: any) => {
              const num = parseFloat(val);
              return acc + (isNaN(num) ? 0 : num);
            },
            0
          );
          const allData = data?.data?.map((val: any) => {
            if (val.apiKey == "total") {
              return {
                ...val,
                value: total,
              };
            }
            if (val.apiKey == "pendingForApproval") {
              return {
                ...val,
                value:
                  (invoiceCount as any)?.data?.["pendingForAGMApproval"] +
                    (invoiceCount as any)?.data?.["pendingForBMApproval"] +
                    (invoiceCount as any)?.data?.["pendingForBoardApproval"] ||
                  0,
              };
            } else {
              return {
                ...val,
                value: (invoiceCount as any)?.data?.[val.apiKey] || 0,
              };
            }
          });

          return {
            ...data,
            data: allData,
            access: !!accessPermission?.[data?.module]?.[data.accessKeys],
          };
        }
        return {
          ...data,
          count: (invoiceCount as any)?.data?.[data?.apiKey] || 0,
          access: !!accessPermission?.[data?.module]?.[data.accessKeys],
        };
      });
      setInvoiceCards([..._cards]);
    }
  }, [invoiceCount]);

  return {
    communityData,
    currentFinanceYear,
    onYearChange,
    invoiceCards,
    onAccordionChange,
    openTabs,
    onExpandCollapse,
    onCreateCardClick,
    onCardClick,
    breadcrumbData,
    onBackClick,
    userCardList,
    isInvoiceCountLoading,
    isUserDataLoading,
    isRefetchingInvoiceCount,
  };
};

export default useInvoice;
